import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { avatarSrc } from "@/lib/defaults";
import { toast } from "sonner";
import { Check, Cake, MessageCircle } from "lucide-react";
import { BirthdayComposer } from "@/components/BirthdayComposer";
import { BIRTHDAY_CARDS, cardUrl } from "@/lib/birthdayKit";

export { BIRTHDAY_CARDS };
const AUTO_CLOSE_MS = 1500;

export const firstNameOf = (name) => (name || "").split(" ")[0];

// État « Envoyé » (façon Facebook) : la carte choisie + « Vous avez souhaité… » + Voir la conversation / Envoyer un autre message.
const WishedState = ({ person, sentCard, onOpenChat, onAgain, t }) => (
  <div className="mt-3 rounded-xl px-3 py-2.5 flex items-center gap-3 bday-wished-in" style={{ background: "var(--yalola-primary-soft)" }} data-testid={`bday-wished-${person.id}`}>
    {sentCard && <img src={cardUrl(sentCard)} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0 bday-card-pop" data-testid={`bday-wished-card-${person.id}`} />}
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold flex items-center gap-1.5" style={{ color: "var(--yalola-text)" }}>
        <Check size={15} style={{ color: "var(--yalola-secondary)" }} />
        <span>{t("birthdays.wished", "Vous avez souhaité un joyeux anniversaire à {name}.").replace("{name}", firstNameOf(person.name))}</span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs font-semibold">
        <button type="button" onClick={onOpenChat} className="inline-flex items-center gap-1 hover:underline" style={{ color: "var(--yalola-primary)" }} data-testid={`bday-open-chat-${person.id}`}>
          <MessageCircle size={13} /> {t("birthdays.open_chat", "Voir la conversation")}
        </button>
        <button type="button" onClick={onAgain} className="hover:underline" style={{ color: "var(--yalola-text-muted)" }} data-testid={`bday-again-${person.id}`}>
          {t("birthdays.send_another", "Envoyer un autre message")}
        </button>
      </div>
    </div>
  </div>
);

const BirthdayRow = ({ person, onWished, onOpenChat }) => {
  const { t } = useApp();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(!!person.wished);
  const [sentCard, setSentCard] = useState(null);   // src de la carte envoyée (ex. /cards/birthday-4.jpg)
  const [again, setAgain] = useState(false);
  const firstName = firstNameOf(person.name);

  const send = async (content, mediaUrl, cardSrc = null) => {
    if (!content && !mediaUrl) return;
    setBusy(true);
    try {
      // La carte « birthday » marque le message : le serveur sait que les vœux de l'année sont envoyés.
      await api.post("/chat/messages", {
        recipient_id: person.id,
        content: content || "",
        ...(mediaUrl ? { media_url: mediaUrl, media_type: "image" } : {}),
        ref: { type: "birthday", id: person.id },
      });
      setSentCard(cardSrc);
      setSent(true);
      setAgain(false);
      window.dispatchEvent(new CustomEvent("yalola:chat", { detail: { type: "sent" } }));
      toast.success(t("birthdays.sent_toast", "Vœux envoyés à {name} 🎉").replace("{name}", firstName));
      onWished?.(person.id);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="py-4 border-b last:border-b-0" style={{ borderColor: "var(--yalola-border)" }} data-testid={`bday-row-${person.id}`} data-wished={sent ? "1" : "0"}>
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10"><AvatarImage src={avatarSrc(person)} /><AvatarFallback>{person.name?.[0]}</AvatarFallback></Avatar>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{person.name}{person.is_me ? ` ${t("birthdays.you", "(vous)")}` : ""}</div>
          {person.is_me && (
            <div className="text-xs font-medium" style={{ color: "var(--yalola-primary)" }}>{t("birthdays.mine", "C'est votre anniversaire 🎂")}</div>
          )}
        </div>
        {person.age != null && <div className="text-xs shrink-0" style={{ color: "var(--yalola-text-muted)" }} data-testid={`bday-age-${person.id}`}>{person.age} {t("birthdays.years", "ans")}</div>}
        {sent && <span className="flex items-center gap-1 text-xs font-medium shrink-0" style={{ color: "var(--yalola-secondary)" }} data-testid={`bday-sent-${person.id}`}><Check size={13} /> {t("birthdays.sent", "Envoyé")}</span>}
      </div>

      {!person.is_me && sent && !again && (
        <WishedState person={person} sentCard={sentCard} t={t} onOpenChat={() => onOpenChat(person)} onAgain={() => setAgain(true)} />
      )}

      {!person.is_me && (!sent || again) && (
        <BirthdayComposer person={person} firstName={firstName} busy={busy} onSend={send} t={t} />
      )}
    </div>
  );
};

// Fenêtre « Anniversaires du jour ». `onWished(id)` remonte au widget ; quand TOUS les vœux du jour ont été envoyés
// (après un nouvel envoi), la fenêtre se ferme seule ~1,5 s plus tard.
export const BirthdaysDialog = ({ open, onOpenChange, birthdays, onWished }) => {
  const { t, openChat } = useApp();
  const [justWished, setJustWished] = useState(() => new Set());
  const closeTimer = useRef(null);
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => { if (!open) { clearTimeout(closeTimer.current); setJustWished(new Set()); } }, [open]);

  const others = birthdays.filter((b) => !b.is_me);
  const handleWished = (id) => {
    const n = new Set(justWished); n.add(id); setJustWished(n);
    onWished?.(id);
    if (others.length > 0 && others.every((o) => o.wished || n.has(o.id))) {
      clearTimeout(closeTimer.current);
      closeTimer.current = setTimeout(() => onOpenChange(false), AUTO_CLOSE_MS);
    }
  };
  const handleOpenChat = (p) => {
    onOpenChange(false);
    openChat({ id: p.id, name: p.name, avatar_url: p.avatar_url, gender: p.gender });
  };
  const allDone = others.length > 0 && others.every((o) => o.wished || justWished.has(o.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[520px] max-h-[85vh] overflow-y-auto" data-testid="birthdays-dialog" data-all-done={allDone ? "1" : "0"}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Cake size={20} style={{ color: "var(--yalola-primary)" }} /> {t("birthdays.dialog_title", "Anniversaires du jour")}
          </DialogTitle>
        </DialogHeader>
        <div>
          {birthdays.map((b) => <BirthdayRow key={b.id} person={b} onWished={handleWished} onOpenChat={handleOpenChat} />)}
        </div>
        {allDone && justWished.size > 0 && (
          <p className="text-center text-xs font-medium pt-1 bday-wished-in" style={{ color: "var(--yalola-secondary)" }} data-testid="birthdays-all-done">
            {t("birthdays.all_done", "Tous les vœux du jour sont envoyés 🎉")}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};
